import { useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 14 — Accents Played with Both Hands.
 *
 * RH plays the tresillo arpeggio (L-M-H, L-M-H, L-M — 3+3+2 across 8 eighths)
 * on Cm – A♭ – E♭ – G, voiced as on Jason's chart: G-C-E♭, A♭-C-E♭, G-B♭-E♭,
 * G-B-D. LH plays one of four things (his chart): hold the root, the accent
 * hits (3+3+2), the pulse (every beat), or the slow pulse (1 and 3). Then flip:
 * LH root-octave-octave on the accents, RH the chords on the pulse.
 *
 * Bespoke visual: the Two-Hand Stack — a side-by-side rhythm grid where
 * the top row is RH (accent) and the bottom row is LH (one of three
 * patterns). You can flip which hand carries the accent.
 */

const LH_MODES = [
  { id: "hold",      label: "Hold the root",          description: "LH plays the root once at bar start and holds it for the whole bar." },
  { id: "accent",    label: "Accent hits (3+3+2)",     description: "LH plays the root on the accents, in unison with the RH groups." },
  { id: "pulse",     label: "Pulse (every beat)",      description: "LH plays 1, 2, 3, 4 whatever the RH accent does — this is where it gets tricky." },
  { id: "slow",      label: "Slow pulse (1 & 3)",      description: "LH hits beats 1 and 3 only — Jason uses it for the chorus." },
] as const;

/** Jason's voicings, semitones above the tonic (in C: G-C-E♭, A♭-C-E♭, G-B♭-E♭, G-B-D). */
const VOICINGS: [number, number, number][] = [[-5, 0, 3], [-4, 0, 3], [-5, -2, 3], [-5, -1, 2]];
/** Duration in eighths of each accent hit: 3 + 3 + 2. */
const ACCENT_LENGTHS: Record<number, number> = { 0: 3, 3: 3, 6: 2 };
type LhMode = typeof LH_MODES[number]["id"];

// 8-cell EIGHTH-note grid for the 3-3-2 accent in RH (cells 0, 3, 6 are accents).
// One bar of 4/4 = 8 eighths, which is what 3+3+2 adds up to.
const RH_ACCENT_CELLS = [true, false, false, true, false, false, true, false];

export default function PianoIntermediate14() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "14");
  return (
    <LessonShell course={course} courseSlug="piano-intermediate" lessonOrder={lesson.order} title={lesson.title} description={lesson.desc} videoUrl={lesson.video} thumbUrl={lesson.thumb} accent={accent}>
      <KeyProvider defaultTonic="C" availableQualities={["natural-minor", "harmonic-minor"]}>
        <PIShell title="Accents Played with Both Hands" exerciseGoal="3-3-2 accents in one hand; hold, accent hits, pulse or slow pulse in the other — then flip the hands." bpm={108} difficulty="Advanced">
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [lhMode, setLhMode] = useState<LhMode>("pulse");
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  // Cm → A♭ → E♭ → G7 progression as semitone offsets from tonic for the chord ROOT
  const progression = [0, 8, 3, 7]; // i, ♭VI, ♭III, V (G is the five of C minor)

  const stop = () => { handleRef.current?.stop(); handleRef.current = null; setRunning(false); setStep(-1); };

  const playLoop = async () => {
    kickAudio();
    stop();
    setRunning(true);
    // 4 bars × 8 eighths = 32 steps, one chord per bar
    const totalSteps = 32;
    const events: { beat: number; notes: string[]; durationSteps: number; velocity?: number }[] = [];
    for (let bar = 0; bar < 4; bar++) {
      const rootSemis = progression[bar];
      const chordRoot = tonicMidi + rootSemis;
      // i is minor; ♭VI and ♭III are MAJOR in a minor key (A♭ = A♭-C-E♭,
      // E♭ = E♭-G-B♭); V is major because it borrows the raised 7th from
      // harmonic minor. Only the tonic is minor here — `bar !== 3` used to
      // make A♭ and E♭ sound minor against their own chord symbols.
      // Jason's voicings (low → high). In C minor: G-C-E♭, A♭-C-E♭, G-B♭-E♭, G-B-D.
      void chordRoot;
      const chord = VOICINGS[bar].map((o) => tonicMidi + o);
      const bassRoot = tonicMidi + rootSemis - 12 - (rootSemis > 6 ? 12 : 0);

      // RH tresillo arpeggio: L-M-H, L-M-H, L-M — the low note returns on each accent
      RH_ACCENT_CELLS.forEach((isAccent, j) => {
        const stepIdx = bar * 8 + j;
        const noteIdx = j % 3;
        const rhNote = chord[noteIdx];
        events.push({
          beat: stepIdx,
          notes: [midiToPitchName(rhNote, k.useFlats)],
          durationSteps: 1,
          velocity: isAccent ? 0.9 : 0.55,
        });
      });

      // LH per mode
      if (lhMode === "hold") {
        events.push({ beat: bar * 8, notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: 8, velocity: 0.7 });
      } else if (lhMode === "accent") {
        RH_ACCENT_CELLS.forEach((isAcc, j) => {
          if (isAcc) events.push({ beat: bar * 8 + j, notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: ACCENT_LENGTHS[j], velocity: 0.85 });
        });
      } else if (lhMode === "pulse") {
        for (const b of [0, 2, 4, 6]) {
          events.push({ beat: bar * 8 + b, notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: 2, velocity: b === 0 ? 0.8 : 0.65 });
        }
      } else {
        // slow pulse on beats 1 and 3 of the bar (steps 0 and 4 of the 8 eighths)
        events.push({ beat: bar * 8,     notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: 4, velocity: 0.8 });
        events.push({ beat: bar * 8 + 4, notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: 4, velocity: 0.7 });
      }
    }
    handleRef.current = await playRhythm(events, {
      bpm: 108, subdivide: 2, totalSteps, loop: true, click: true, beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
  };

  // Master keyboard: light up the active bar's chord
  const activeBar = step >= 0 ? Math.floor(step / 8) : 0;
  const activeChordRoot = progression[activeBar];
  // Only the i chord is minor: ♭VI and ♭III are major in a minor key, and V borrows the raised 7th.
  const thirdSemis = activeBar === 0 ? 3 : 4;
  const masterHighlights: KeyHighlight[] = [
    { pc: ((k.tonicPc + activeChordRoot) % 12 + 12) % 12, tone: "root", label: "1" },
    { pc: ((k.tonicPc + activeChordRoot + thirdSemis) % 12 + 12) % 12, tone: "voice", label: thirdSemis === 3 ? "♭3" : "3" },
    { pc: ((k.tonicPc + activeChordRoot + 7) % 12 + 12) % 12, tone: "voice", label: "5" },
  ];
  const chordNames = progression.map((r, i) => k.nameForPc((k.tonicPc + r) % 12) + (i === 0 ? "m" : ""));

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={masterHighlights} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Master keyboard reflects whichever chord of the four-bar progression is currently sounding.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Two hands, two rhythmic jobs">
          <p>
            Up to now, both hands have mostly shared a rhythm. In this lesson they
            <strong> intentionally diverge</strong>. The right hand carries an accented
            arpeggio (the 3-3-2 tresillo you've met before). The left hand picks ONE of four
            rhythmic jobs — holding the root, the accent hits, the pulse or the slow pulse — and
            stays in that lane for the whole section. Each combination sounds like a different
            section of the song.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "What if you can use the concept of accents along with the pulse, the way we move to
            music, the natural, I guess, head bobbing movement whenever you're listening to a song."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Pick LH" title="Four left-hand modes — same RH">
          <div className="my-3 grid grid-cols-1 gap-2 md:grid-cols-4">
            {LH_MODES.map((m) => (
              <motion.button key={m.id} layout onClick={() => { stop(); setLhMode(m.id); }}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  lhMode === m.id ? "border-amber-400/60 bg-amber-500/10" : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                }`}>
                <div className={`text-[14px] font-bold ${lhMode === m.id ? "text-amber-200" : "text-cyan-100"}`}>{m.label}</div>
                <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">{m.description}</div>
              </motion.button>
            ))}
          </div>
          <TwoHandStack lhMode={lhMode} step={running ? step : -1} chordNames={chordNames} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={playLoop} disabled={running} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50">▶ Loop the four-bar progression</button>
            {running && <button onClick={stop} className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20">■ Stop</button>}
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Flip it" title="Now make the LEFT hand play the accent">
          <p>
            Once the right-hand-accent / left-hand-pulse combo feels stable, swap the hands. On
            Jason's chart the LH carries the 3+3+2 as a bass line — the root, then the octave, then
            the octave again — while the RH plays the chords on the pulse, every beat. Same
            musical shape, completely different colour.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "What am I doing in the right hand? I'm complementing playing the pulse of all the
            chords so you could flip your hands and develop a different rhythmic pattern."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Songcraft" title="One progression, multiple sections">
          <p>
            The point of this drill goes beyond fingers. <strong>By changing the LH mode, you turn
            one chord progression into more than one section of a song</strong> without changing
            the chords — bring the pulse back and it sounds like another section; Jason moves to
            the slow pulse at the chorus. Mix in staccato versus legato in the left hand too.
          </p>
          <PIInsight>
            <p>
              The harmony is identical in every section; only the rhythmic job of each hand
              changes.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Apply to a melody" title="Carry a tune over the rhythm">
          <p>
            Once each LH mode is stable on its own, take a melody you know — say <em>Ode to Joy</em>
            — and play it in the right hand on top of the accent. The melody floats above the
            3-3-2 accent. The student practising this should feel like they're playing a band, not
            a piano.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The full four-bar loop">
          <LessonMidi courseSlug="piano-intermediate" order="14" label="Two-hand accent loop" originalBpm={108} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={<>One LH mode per rung. Don't add the next mode until the current one is locked.</>}
            rungs={[
              { title: "Hold the root + RH accent",   minutes: 4, accent: "amber",   detail: "LH strikes the root once per bar; let it ring. RH carries the 3-3-2 arpeggio." },
              { title: "LH plays the accent hits",    minutes: 4, accent: "cyan",    detail: "Both hands land on the 3+3+2 accents — both hands moving together." },
              { title: "Pulse, then slow pulse",       minutes: 4, accent: "rose",    detail: "LH on every beat, irrespective of the RH accent — the tricky one. Then only beats 1 and 3." },
              { title: "Flip the hands",               minutes: 4, accent: "emerald", detail: "Now LH carries the accent as a bass line (root, octave, octave) and RH plays the chords on the pulse. Same maths, very different feel." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

function TwoHandStack({ lhMode, step, chordNames }: { lhMode: LhMode; step: number; chordNames: string[] }) {
  // 32 cells = 4 bars × 8 eighths
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">▤ Two-hand rhythm stack · 4 bars</div>
        <div className="font-mono text-[11px] text-cyan-300/80">3-3-2 accent · 8ths</div>
      </div>
      {/* RH row */}
      <div className="mb-1 flex items-center gap-2">
        <span className="w-12 font-mono text-[10px] uppercase tracking-wider text-cyan-300">RH</span>
        <div className="grid flex-1 gap-0.5" style={{ gridTemplateColumns: "repeat(32, minmax(0, 1fr))" }}>
          {Array.from({ length: 32 }, (_, i) => {
            const cellInBar = i % 8;
            const isAccent = RH_ACCENT_CELLS[cellInBar];
            const isActive = i === step;
            return (
              <motion.div key={i} animate={{ scale: isActive ? 1.18 : 1 }} transition={{ duration: 0.1 }}
                className={`h-7 rounded ${isAccent ? "bg-amber-400" : "bg-amber-500/25"}`} />
            );
          })}
        </div>
      </div>
      {/* LH row */}
      <div className="flex items-center gap-2">
        <span className="w-12 font-mono text-[10px] uppercase tracking-wider text-cyan-300">LH</span>
        <div className="grid flex-1 gap-0.5" style={{ gridTemplateColumns: "repeat(32, minmax(0, 1fr))" }}>
          {Array.from({ length: 32 }, (_, i) => {
            const cellInBar = i % 8;
            const isHit =
              lhMode === "hold"   ? cellInBar === 0
              : lhMode === "accent" ? RH_ACCENT_CELLS[cellInBar]
              : lhMode === "pulse"  ? cellInBar % 2 === 0
              :                      cellInBar === 0 || cellInBar === 4;
            const isActive = i === step;
            return (
              <motion.div key={i} animate={{ scale: isActive ? 1.18 : 1 }} transition={{ duration: 0.1 }}
                className={`h-7 rounded ${isHit ? "bg-cyan-400" : "bg-cyan-500/15"}`} />
            );
          })}
        </div>
      </div>
      {/* Bar dividers */}
      <div className="mt-1 grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wider text-cyan-300/70">
        {chordNames.map((n, i) => (
          <div key={i} className="text-center">{n}</div>
        ))}
      </div>
    </div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (<motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>{children}</motion.div>);
}
function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>{children}</blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
